import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/Button";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Seo from "@/components/Seo";
import PartnerMarquee from "@/components/PartnerMarquee";
import { services, solutions, process, stats, partners, ArrowRight } from "@/data/site";

const productMeta = {
  Zoho: { logo: "/zoho-logo.png", to: "/products/zoho" },
  Adobe: { logo: "/adobe-logo.webp", to: "/products" },
  Razorpay: { logo: "/razorpay-logo.webp", to: "/products" },
  Absolute: { logo: "/absolute-logo.webp", to: "/products/absolute" },
  Hostinger: { logo: "/hostinger-logo.webp", to: "/hostinger-offer", offer: "20% Off" },
};
const productCards = partners.map((p) => ({ ...p, ...productMeta[p.name] }));

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <div data-testid="home-page">
      <Seo page="home" title="ThinkSmiths — Software Services for the Modern Enterprise"
        description="Custom software, app development, AI agents, Google Search solutions, managed hosting and enterprise implementations (Zoho, Adobe, Razorpay, Absolute, Hostinger)."
        keywords="software services, custom software development, AI agents, managed hosting, Zoho, Adobe, Razorpay, Hostinger" />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-40 pb-24 overflow-hidden">
        <div className="max-w-8xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="label text-xs text-cobalt mb-6">01 / Software Services</div>
            </Reveal>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-ink leading-[1.02] tracking-tight">
              <MaskedLines lines={["Engineering", <>the <span className="font-extrabold">Modern</span></>, "Enterprise."]} delay={0.1} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-8 text-lg text-ink max-w-xl leading-relaxed">
              We build software and provide services that feels like your favourite conversation — not another sales pitch. From custom development to enterprise implementations, we help ambitious brands grow their stack to success.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4">
              <Button to="/contact" data-testid="hero-cta-primary">Request a Free Consultation <ArrowRight size={16} /></Button>
              <Button to="/services" variant="secondary" data-testid="hero-cta-secondary">Explore Services</Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
              className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4">
              {stats.slice(0, 3).map((s, i) => (
                <div key={i} className="flex items-center gap-8">
                  <div>
                    <div className="font-cabinet font-extrabold text-3xl text-ink">{s.value}</div>
                    <div className="text-xs text-ink mt-1">{s.label}</div>
                  </div>
                  {i < 2 && <div className="w-px h-10 bg-slate-200" />}
                </div>
              ))}
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_24px_70px_rgb(15,23,42,0.10)]">
              <img src="https://images.pexels.com/photos/3747070/pexels-photo-3747070.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=780&w=620" alt="ThinkSmiths team" className="w-full h-[520px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl border border-slate-200 px-5 py-4">
                <div className="label text-[10px] text-cobalt">Now partnering with</div>
                <div className="font-cabinet font-bold text-ink mt-1">Zoho · Adobe · Razorpay · Absolute · Hostinger</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <PartnerMarquee />

      {/* SERVICES BENTO */}
      <section className="py-28" data-testid="home-services">
        <div className="max-w-8xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <div className="label text-xs text-cobalt mb-4">02 / What We Do</div>
            <h2 className="text-4xl sm:text-5xl font-light text-ink tracking-tight leading-tight">Your digital partners who <span className="font-extrabold">know their stuff.</span></h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              const wide = i === 0;
              return (
                <Reveal key={s.slug} delay={i * 0.06} className={wide ? "md:col-span-2" : ""}>
                  <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}
                    className="group block h-full border border-slate-200 rounded-2xl p-10 bg-white hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(15,23,42,0.06)] transition-[transform,box-shadow,border-color] hover:border-cobalt/30">
                    <div className="flex items-center justify-between mb-6">
                      <span className="label text-[10px] text-cobalt">{String(i + 1).padStart(2, "0")}</span>
                      <Icon className="text-cobalt" size={26} />
                    </div>
                    <h3 className="font-cabinet font-bold text-2xl text-ink mb-3">{s.title}</h3>
                    <p className="text-ink">{s.short}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-cobalt group-hover:gap-2 transition-all">Learn more <ArrowRight size={15} /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNER PRODUCTS */}
      <section className="bg-white border-y border-slate-200 py-28" data-testid="home-products">
        <div className="max-w-8xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <div className="label text-xs text-cobalt mb-4">03 / Products &amp; Partnerships</div>
            <h2 className="text-4xl sm:text-5xl font-light text-ink tracking-tight leading-tight">Enterprise software, <span className="font-extrabold">set up right.</span></h2>
            <p className="mt-6 text-ink">As an official partner and reseller, we implement, integrate and support the platforms that power modern businesses.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCards.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06}>
                <Link to={p.to} data-testid={`home-product-${p.name.toLowerCase()}`}
                  className="group relative flex flex-col h-full border border-slate-200 rounded-2xl p-8 bg-mist/40 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(15,23,42,0.06)] hover:border-cobalt/30 transition-[transform,box-shadow,border-color]">
                  {p.offer && (
                    <span className="absolute top-4 right-4 text-[10px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: "#673DE6" }} data-testid={`home-product-${p.name.toLowerCase()}-offer`}>{p.offer}</span>
                  )}
                  <div className="h-9 flex items-center mb-6"><img src={p.logo} alt={p.name} className="max-h-8 w-auto object-contain" /></div>
                  <div className="label text-[10px] text-cobalt mb-2">{p.tag}</div>
                  <p className="text-ink text-sm flex-1">{p.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-cobalt group-hover:gap-2 transition-all">Learn more <ArrowRight size={15} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS / MANIFESTO */}
      <section className="py-28 on-dark border-y" data-testid="home-process">
        <div className="max-w-8xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <div className="label text-xs text-cobalt mb-4">04 / Our Process</div>
            <h2 className="text-4xl font-light text-ink tracking-tight leading-tight">How we make the <span className="font-extrabold">magic happen.</span></h2>
            <p className="mt-6 text-ink">Behind every click there's a real person making a decision. We start with connection, then strategy follows.</p>
            <Button to="/about" variant="ghost" className="mt-6 px-0">More about us <ArrowRight size={15} /></Button>
          </Reveal>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-y-12 gap-x-10">
            {process.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="border-t border-slate-300 pt-6">
                <div className="font-cabinet font-extrabold text-4xl text-cobalt/20">{p.n}</div>
                <h3 className="font-cabinet font-bold text-xl text-ink mt-2">{p.title}</h3>
                <p className="text-ink mt-2">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-28" data-testid="home-solutions">
        <div className="max-w-8xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <div className="label text-xs text-cobalt mb-4">05 / Who We Help</div>
            <h2 className="text-4xl sm:text-5xl font-light text-ink tracking-tight leading-tight">Solutions built <span className="font-extrabold">around you.</span></h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {solutions.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div className="h-full border border-slate-200 rounded-2xl p-10 bg-white">
                    <Icon className="text-cobalt mb-6" size={28} />
                    <h3 className="font-cabinet font-bold text-2xl text-ink mb-3">{s.title}</h3>
                    <p className="text-ink">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-ink text-white" data-testid="home-cta">
        <div className="max-w-8xl mx-auto px-6 text-center">
          <Reveal>
            <div className="label text-xs text-white/70 mb-6">Let's Cut Through The Noise</div>
            <h2 className="text-5xl sm:text-7xl font-light tracking-tight leading-none text-white">Let's <span className="font-extrabold" style={{ color: "#5b8def" }}>build</span> something<br />worth talking about.</h2>
            <div className="mt-10 flex justify-center">
              <Button to="/contact" data-testid="cta-contact">Request a Free Consultation <ArrowRight size={16} /></Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
